import Papa from "papaparse";
import JSZip from "jszip";
import { parseAmount } from "./payrollModel";

export const FIELDS = {
  name: "Full name",
  employeeId: "Employee ID",
  email: "Email",
  amount: "Amount to pay",
  currency: "Currency",
  account: "Bank account / IBAN",
};
const aliases = {
  name: ["name", "full name", "employee name"],
  employeeId: ["employee id", "employeeid", "id"],
  email: ["email", "work email"],
  amount: ["amount", "amount to pay", "net salary", "salary"],
  currency: ["currency"],
  account: ["account", "iban", "bank account", "bank account / iban"],
};
export const suggestMapping = (headers) =>
  Object.fromEntries(
    Object.entries(aliases).map(([key, names]) => [
      key,
      headers.find((h) => names.includes(h.trim().toLowerCase())) || "",
    ]),
  );
const xml = (text) => {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror"))
    throw new Error(
      "This spreadsheet could not be read. Please export it as CSV.",
    );
  return doc;
};
export async function readSpreadsheet(file) {
  if (file.size > 5 * 1024 * 1024)
    throw new Error("Choose a spreadsheet smaller than 5 MB.");
  let matrix;
  if (/\.csv$/i.test(file.name)) {
    const result = Papa.parse(await file.text(), { skipEmptyLines: "greedy" });
    if (result.errors.length)
      throw new Error(
        "The CSV has invalid quoting or formatting. Please export it again.",
      );
    matrix = result.data;
  } else if (/\.xlsx$/i.test(file.name)) {
    const archive = await JSZip.loadAsync(await file.arrayBuffer());
    const read = async (path) => {
      const entry = archive.file(path);
      if (!entry || entry._data?.uncompressedSize > 12 * 1024 * 1024)
        throw new Error(
          "The workbook is missing a sheet or is too large. Export the payroll sheet as CSV.",
        );
      return xml(await entry.async("string"));
    };
    const workbook = await read("xl/workbook.xml");
    const relations = await read("xl/_rels/workbook.xml.rels");
    const first = workbook.getElementsByTagName("sheet")[0];
    const relationship = [
      ...relations.getElementsByTagName("Relationship"),
    ].find((r) => r.getAttribute("Id") === first?.getAttribute("r:id"));
    const target = relationship?.getAttribute("Target") || "";
    if (!target || target.includes("..") || /https?:/.test(target))
      throw new Error("Unsupported workbook. Export the payroll sheet as CSV.");
    const sheet = await read(
      target.startsWith("/") ? target.slice(1) : `xl/${target}`,
    );
    let strings = [];
    if (archive.file("xl/sharedStrings.xml"))
      strings = [
        ...(await read("xl/sharedStrings.xml")).getElementsByTagName("si"),
      ].map((s) =>
        [...s.getElementsByTagName("t")].map((t) => t.textContent).join(""),
      );
    matrix = [...sheet.getElementsByTagName("row")]
      .map((row) => {
        const values = [];
        for (const cell of row.getElementsByTagName("c")) {
          if (cell.getElementsByTagName("f").length)
            throw new Error(
              "Formula cells are not imported. Paste values into your payroll sheet first.",
            );
          const letters = (cell.getAttribute("r") || "").replace(/\d/g, "");
          const index =
            [...letters].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0) - 1;
          if (!Number.isInteger(index) || index < 0 || index > 100)
            throw new Error("Use a payroll sheet with at most 101 columns.");
          const value = cell.getElementsByTagName("v")[0]?.textContent || "";
          values[index] =
            cell.getAttribute("t") === "s"
              ? strings[Number(value)]
              : cell.getAttribute("t") === "inlineStr"
                ? [...cell.getElementsByTagName("t")]
                    .map((t) => t.textContent)
                    .join("")
                : value;
        }
        return values;
      })
      .filter((row) => row.some((v) => String(v || "").trim()));
  } else throw new Error("Choose an .xlsx or .csv file.");
  if (matrix.length < 2 || matrix.length > 1001)
    throw new Error("Include a header and between 1 and 1,000 employee rows.");
  const headers = Array.from(matrix[0], (v) => String(v || "").trim());
  if (headers.some((h) => !h) || new Set(headers).size !== headers.length)
    throw new Error("Every column needs a unique, non-empty heading.");
  return {
    headers,
    rows: matrix
      .slice(1)
      .map((row) =>
        Object.fromEntries(
          headers.map((h, i) => [h, String(row[i] ?? "").trim()]),
        ),
      ),
    filename: file.name,
  };
}
export function mapRows(sheet, mapping) {
  if (!mapping.name || (!mapping.email && !mapping.employeeId))
    throw new Error("Map Full name and either Email or Employee ID.");
  const selected = Object.values(mapping).filter(Boolean);
  if (new Set(selected).size !== selected.length)
    throw new Error("Assign each spreadsheet column to only one field.");
  return sheet.rows.map((data, i) => {
    const row = Object.fromEntries(
      Object.keys(FIELDS).map((key) => [key, data[mapping[key]] || ""]),
    );
    if (!row.name || (!row.email && !row.employeeId))
      throw new Error(`Row ${i + 2}: add a name and an email or employee ID.`);
    if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email))
      throw new Error(`Row ${i + 2}: check the email address.`);
    row.currency = row.currency.toUpperCase();
    if (row.currency && !["USD", "LBP"].includes(row.currency))
      throw new Error(`Row ${i + 2}: use USD or LBP.`);
    try {
      row.amount = parseAmount(row.amount);
    } catch (error) {
      throw new Error(`Row ${i + 2}: ${error.message}`);
    }
    return row;
  });
}
export function downloadCSV(filename, rows) {
  const csv = Papa.unparse(
    rows.map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [
          key,
          typeof value === "string" && /^[=+@\-\t\r]/.test(value)
            ? `'${value}`
            : value,
        ]),
      ),
    ),
  );
  const url = URL.createObjectURL(
    new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const sampleSheet = {
  filename: "example-payroll.csv",
  headers: Object.values(FIELDS),
  rows: [
    {
      "Full name": "Nour Khoury",
      "Employee ID": "E101",
      Email: "nour@example.com",
      "Amount to pay": "1550",
      Currency: "USD",
      "Bank account / IBAN": "DEMO-1001",
    },
    {
      "Full name": "Rami Nasser",
      "Employee ID": "",
      Email: "rami@example.com",
      "Amount to pay": "1800",
      Currency: "USD",
      "Bank account / IBAN": "DEMO-1004",
    },
    {
      "Full name": "Jad Daher",
      "Employee ID": "E105",
      Email: "jad@example.com",
      "Amount to pay": "1300",
      Currency: "USD",
      "Bank account / IBAN": "DEMO-1005",
    },
    {
      "Full name": "Maya Saad",
      "Employee ID": "",
      Email: "maya.new@example.com",
      "Amount to pay": "1200",
      Currency: "USD",
      "Bank account / IBAN": "DEMO-1003",
    },
  ],
};
